import React, { useState } from 'react';
import { Mail, MessageCircle, MapPin, Phone, Send } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { contactAPI } from '../services/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    
    try {
      await contactAPI.submit(formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: ''
      });
      
      setTimeout(() => setSubmitStatus(null), 5000);
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Failed to submit form. Please try again or contact us directly via WhatsApp.');
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    'Digital Marketing Strategy',
    'Social Media Marketing',
    'Paid Advertising (PPC)',
    'Search Engine Optimization',
    'Website & Funnel Optimization',
    'Brand Promotion & Growth',
    'Ebook & Book Promotion',
    'Shopify Development & Marketing',
    'General Inquiry'
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0D234E] to-[#1a3a6e] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Let's Start Growing
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Ready to take your business to the next level? Get in touch with us today for a free consultation
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-[#0D234E] mb-6">
                Get in Touch
              </h2>
              <p className="text-gray-700 leading-relaxed mb-8 text-lg">
                We're here to help you achieve your digital marketing goals. Whether you're looking to increase sales, build your brand, or generate more leads, we have the expertise to make it happen.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#BFB47E] bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#0D234E]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0D234E] mb-1">Email</h3>
                    <a
                      href={`mailto:${contactInfo.email}`}
                      className="text-gray-700 hover:text-[#BFB47E] transition-colors"
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#BFB47E] bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="text-[#0D234E]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0D234E] mb-1">WhatsApp</h3>
                    <a
                      href={contactInfo.whatsapp}
                      data-testid="contact-phone-whatsapp"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-700 hover:text-[#BFB47E] transition-colors"
                    >
                      {contactInfo.whatsappDisplay}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#BFB47E] bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#0D234E]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0D234E] mb-1">Location</h3>
                    <p className="text-gray-700">{contactInfo.address}</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 bg-[#BFB47E] bg-opacity-10 rounded-xl">
                <h3 className="text-xl font-bold text-[#0D234E] mb-3">
                  Prefer to Chat Directly?
                </h3>
                <p className="text-gray-700 mb-4">
                  Get instant answers to your questions on WhatsApp
                </p>
                <a
                  href={contactInfo.whatsapp}
                  data-testid="contact-chat-whatsapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-[#0D234E] text-white px-6 py-3 rounded-md font-semibold hover:bg-[#1a3a6e] transition-colors"
                >
                  <MessageCircle size={20} className="mr-2" />
                  Chat on WhatsApp
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gray-50 p-8 rounded-xl">
              <h2 className="text-3xl font-bold text-[#0D234E] mb-6">
                Send Us a Message
              </h2>

              {submitStatus === 'success' && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md mb-6">
                  Thank you! We'll get back to you within 24 hours.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-6">
                  {errorMessage}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0D234E]"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0D234E]"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0D234E]"
                    placeholder="+234 800 000 0000"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0D234E]"
                    placeholder="Your Company"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                    Service Interested In *
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0D234E]"
                  >
                    <option value="">Select a service</option>
                    {services.map((service) => (
                      <option key={service} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0D234E]"
                    placeholder="Tell us about your project and goals..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#0D234E] text-white px-8 py-4 rounded-md font-semibold hover:bg-[#1a3a6e] transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send size={20} className="ml-2" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0D234E] mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            {[
              {
                question: 'How quickly can I expect to see results?',
                answer: 'Results vary depending on the service and your specific goals. Paid advertising campaigns can show results within days, while SEO typically shows significant improvements within 3-6 months. We provide regular reports to track progress.'
              },
              {
                question: 'What makes DMARKETINGCHAMP different from other agencies?',
                answer: 'We focus on delivering measurable ROI, not just vanity metrics. Every strategy is customized to your business, and we maintain transparent communication throughout our partnership. Our track record speaks for itself with an average 3x-10x growth for our clients.'
              },
              {
                question: 'Do you work with businesses of all sizes?',
                answer: 'Yes! We work with solo entrepreneurs, small businesses, and established brands. Our strategies scale to your budget and goals, ensuring you get maximum value regardless of your business size.'
              },
              {
                question: 'What is your pricing structure?',
                answer: 'Our pricing is customized based on your specific needs, goals, and the services required. We offer flexible packages and can work within various budgets. Contact us for a free consultation and custom quote.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-lg font-bold text-[#0D234E] mb-2">
                  {faq.question}
                </h3>
                <p className="text-gray-700">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
